import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2 } from 'lucide-react';

import useProgressHeartbeat from './useProgressHeartbeat';

const Wrap = styled.div`
  background: #fff;
  border: 1px solid #E0E4E8;
  border-radius: 6px;
  overflow: hidden;

  &:fullscreen {
    border-radius: 0;
    border: none;
    display: flex;
    flex-direction: column;
    background: #0F172A;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #F8FAFC;
  border-bottom: 1px solid #E0E4E8;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NavBtn = styled.button`
  background: transparent;
  border: 1px solid #CBD5E1;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.primary};
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Stage = styled.div`
  background: #0F172A;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  padding: 16px;

  ${Wrap}:fullscreen & {
    flex: 1;
    min-height: 0;
  }
`;

const SlideImg = styled.img`
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
  background: #fff;
  border-radius: 2px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.4);

  ${Wrap}:fullscreen & {
    max-height: calc(100vh - 56px);
  }
`;

/**
 * Per-slide PPTX viewer. Each slide is a PNG generated by the backend
 * conversion job. Mirrors PdfViewer's progress contract: viewedPages is
 * the set of slide numbers the learner has reached, and clientCompleted
 * fires when every slide has been seen at least once. Going back is
 * always allowed; the heartbeat preserves max-reached server-side.
 */
export default function SlideViewer({
  contentItem,
  assignmentId,
  onCompleted,
  onProgress,
}) {
  const { t } = useTranslation();
  const slideUrls = contentItem.slideUrls || [];
  const slideCount = slideUrls.length;
  const wrapRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [page, setPage] = useState(() => {
    const seen = contentItem.viewedPages || [];
    return seen.length > 0 ? Math.min(Math.max(...seen), slideCount || 1) : 1;
  });

  const viewedRef = useRef(new Set(contentItem.viewedPages || [1]));
  // Avoid re-firing onCompleted on every heartbeat after completion — that
  // would unmount the image and the user would see a constant refresh.
  const completedFiredRef = useRef(contentItem.isCompleted === true);

  const getState = useCallback(() => ({
    contentItemId: contentItem.id,
    viewedPages: Array.from(viewedRef.current),
    clientCompleted: slideCount > 0 && viewedRef.current.size >= slideCount,
  }), [contentItem.id, slideCount]);

  const flushRef = useRef(null);
  flushRef.current = useProgressHeartbeat(assignmentId, getState, (res) => {
    if (res?.isCompleted && !completedFiredRef.current) {
      completedFiredRef.current = true;
      onCompleted?.();
    }
    onProgress?.(res);
  });

  useEffect(() => {
    viewedRef.current.add(page);
    flushRef.current?.();
  }, [page]);

  // Keep the local fullscreen flag in sync with the browser — covers ESC,
  // F11, and OS-level exits that don't go through our toggle button.
  useEffect(() => {
    const onChange = () => setIsFullscreen(document.fullscreenElement === wrapRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(slideCount, p + 1));

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      wrapRef.current?.requestFullscreen?.();
    }
  };

  if (slideCount === 0) return null;

  const currentUrl = slideUrls[page - 1];

  return (
    <Wrap ref={wrapRef}>
      <Toolbar>
        <div>{contentItem.originalFileName || 'PPTX'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavBtn type="button" onClick={goPrev} disabled={page <= 1}>
            <ChevronLeft size={14} /> {t('training.prevSlide')}
          </NavBtn>
          <span style={{ minWidth: 70, textAlign: 'center' }}>
            {t('training.slideNofM', { n: page, m: slideCount })}
          </span>
          <NavBtn type="button" onClick={goNext} disabled={page >= slideCount}>
            {t('training.nextSlide')} <ChevronRight size={14} />
          </NavBtn>
          <NavBtn
            type="button"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('training.exitFullscreen') : t('training.enterFullscreen')}
          >
            {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </NavBtn>
        </div>
      </Toolbar>
      <Stage>
        <SlideImg
          src={currentUrl}
          alt={t('training.slideNofM', { n: page, m: slideCount })}
        />
      </Stage>
    </Wrap>
  );
}
